import { LegalLayout } from '../components/LegalLayout';

export function TermsPage() {
  return (
    <LegalLayout title="Terms of Service" lastUpdated="July 14, 2026">
      <p>
        These Terms of Service (the <strong>"Terms"</strong>) govern your access to
        and use of PROTBYTE Sentinel (the <strong>"Service"</strong>), a free,
        self-service external security assessment tool operated by{' '}
        <strong>PROTBYTE</strong> (<strong>"PROTBYTE"</strong>,{' '}
        <strong>"we"</strong>, <strong>"us"</strong>, <strong>"our"</strong>). By
        accessing or using the Service, you agree to these Terms, our{' '}
        <a href="/aup">Acceptable Use Policy</a>, and our{' '}
        <a href="/privacy">Privacy Policy</a>, which are incorporated here by
        reference. <strong>If you do not agree, do not use the Service.</strong>
      </p>

      <h2>1. The Service</h2>
      <p>
        PROTBYTE Sentinel inspects publicly observable signals about a domain you
        submit and produces a heuristic security score and an executive-style
        report mapped to common compliance frameworks. The signals analyzed
        include:
      </p>
      <ul>
        <li>DNS configuration (DNSSEC, CAA, A/AAAA/MX/NS/TXT/SOA records, nameserver redundancy);</li>
        <li>email authentication records (SPF, DKIM, DMARC, BIMI, MTA-STS, TLS-RPT);</li>
        <li>web security headers, TLS-certificate metadata, and the security.txt policy (RFC 9116);</li>
        <li>WHOIS / RDAP registration data (including domain age);</li>
        <li>hosting and network intelligence (reverse DNS and IP-to-ASN);</li>
        <li>certificate-transparency log entries (subdomain enumeration and dangling-CNAME takeover risk);</li>
        <li>tech-stack fingerprints from response headers, HTML, JS bundles, robots.txt and manifests;</li>
        <li>SaaS-vendor signals inferred from your DNS (MX records, TXT verifications, homepage links, operational subdomains, DMARC reporting endpoints);</li>
        <li>publicly-accessible exposure checks (probes for paths such as <code>.git</code>, <code>.env</code>, admin panels);</li>
        <li>CVE matching for detected components against the OSV.dev database.</li>
      </ul>
      <p>
        The Service relies on public third-party data sources, including
        Cloudflare DNS-over-HTTPS, the IANA RDAP bootstrap registry and
        TLD-operated RDAP servers, certspotter and crt.sh (Certificate
        Transparency mirrors), Team Cymru (IP-to-ASN), and OSV.dev.
      </p>
      <p>
        The Service is a <strong>browser-based tool</strong> intended to be used
        through the PROTBYTE Sentinel website. It is not a public, programmatic, or
        bulk-scanning API. Requests to the scan endpoint are protected by a
        human-verification challenge (Cloudflare Turnstile), and automated,
        scripted, or high-volume access is prohibited under our{' '}
        <a href="/aup">Acceptable Use Policy</a>.
      </p>

      <h2>2. Eligibility and authorization</h2>
      <p>
        You must be at least 18 years old, or the age of majority in your
        jurisdiction, and legally able to enter into these Terms, to use the
        Service.
      </p>
      <p>
        <strong>
          You represent and warrant that, for every domain you submit, you are the
          owner of that domain or you have obtained explicit, current authorization
          from the owner to perform an external security assessment of it.
        </strong>{' '}
        You are solely responsible for ensuring that your use of the Service is
        lawful in your jurisdiction and in the jurisdiction of the target domain.
        Submitting domains you do not own or are not authorized to assess may
        violate applicable laws, including computer-misuse and unauthorized-access
        statutes (for example, the U.S. Computer Fraud and Abuse Act and analogous
        laws elsewhere). You bear full responsibility for such use, and you accept
        all associated risk and liability.
      </p>

      <h2>3. Acceptable use</h2>
      <p>
        Your use of the Service is subject to our{' '}
        <a href="/aup">Acceptable Use Policy</a>. Among other things, you agree not
        to:
      </p>
      <ul>
        <li>scan domains you do not own or otherwise have permission to assess;</li>
        <li>use the Service to attack, compromise, disrupt, or gain unauthorized
            access to any system, or to facilitate any of the foregoing;</li>
        <li>circumvent or attempt to circumvent the rate limit, the
            human-verification challenge, authentication, or any other technical
            control;</li>
        <li>access the Service through automated, scripted, headless, or bulk
            means;</li>
        <li>resell, sublicense, or commercially redistribute the Service or its
            output without our prior written consent;</li>
        <li>use the Service for any unlawful, harassing, or abusive purpose, or in
            a manner that infringes third-party rights.</li>
      </ul>
      <p>
        We may rate-limit, throttle, suspend, or block any client that we
        reasonably believe is abusing the Service or violating these Terms or the
        Acceptable Use Policy, at any time and without notice.
      </p>

      <h2>4. Not professional advice</h2>
      <p>
        The Service and its output are provided for general informational purposes
        only. They do <strong>not</strong> constitute legal, regulatory, security,
        compliance, financial, or other professional advice, and must not be relied
        upon as such. The Service is <strong>not</strong> a penetration test, a
        security audit, a compliance audit, or a certification, and it does not
        establish, attest to, or guarantee compliance with any standard or
        framework.
      </p>
      <p>
        Compliance-framework mapping (for example, SOC 2, ISO 27001, NIST CSF, PCI
        DSS, NIST 800-171, or CMMC) is a heuristic indication derived solely from
        externally observable signals. It is not an assessment by a qualified
        auditor and confers no certification or attestation of any kind. You should
        consult qualified security and legal professionals before making decisions
        based on the Service.
      </p>

      <h2>5. No warranty</h2>
      <p>
        The Service is provided <strong>"as is"</strong> and{' '}
        <strong>"as available"</strong> without warranties of any kind, whether
        express, implied, or statutory, including any implied warranties of
        merchantability, fitness for a particular purpose, title, accuracy, and
        non-infringement. Findings are heuristic and derived from public data; they
        may be incomplete, stale, inaccurate, or wrong, and may include both false
        positives and false negatives.
      </p>
      <p>
        We do not warrant that the Service will be uninterrupted, secure, timely, or
        error-free, that defects will be corrected, or that any third-party data
        source will return accurate or timely results. No advice or information,
        whether oral or written, obtained from the Service creates any warranty not
        expressly stated in these Terms.
      </p>

      <h2>6. Assumption of risk</h2>
      <p>
        You use the Service entirely at your own risk. You are solely responsible
        for any decision or action you take, or fail to take, based on the Service
        or its output, and for any consequences of submitting a domain to the
        Service.
      </p>

      <h2>7. Limitation of liability</h2>
      <p>
        To the maximum extent permitted by law, in no event will PROTBYTE or
        its owners, affiliates, officers, employees, contractors, or licensors be
        liable for any indirect, incidental, special, consequential, exemplary, or
        punitive damages, or for any loss of profits, revenues, data, goodwill, or
        business opportunity, arising out of or related to your use of (or
        inability to use) the Service, whether based on warranty, contract, tort
        (including negligence), strict liability, or any other legal theory, even if
        we have been advised of the possibility of such damages.
      </p>
      <p>
        To the maximum extent permitted by law, our total aggregate liability for
        all claims arising out of or relating to these Terms or the Service will not
        exceed the greater of (a) the total amount you paid us to use the Service
        during the three months preceding the event giving rise to the claim, which
        for a free service is <strong>USD $0.00</strong>, or (b){' '}
        <strong>USD $0.00</strong>. Some jurisdictions do not allow certain
        exclusions or limitations, so some of the above may not apply to you; in
        that case, our liability is limited to the smallest amount permitted by law.
      </p>

      <h2>8. Indemnification</h2>
      <p>
        You agree to defend, indemnify, and hold harmless PROTBYTE and its
        owners, affiliates, officers, employees, contractors, and licensors from and
        against any and all claims, demands, liabilities, damages, losses, costs,
        and expenses (including reasonable attorneys' fees) arising out of or related
        to: (a) your use of the Service; (b) your violation of these Terms, the
        Acceptable Use Policy, or applicable law; (c) your submission of any domain
        you did not own or were not authorized to assess; or (d) your violation of
        any third-party right.
      </p>

      <h2>9. Intellectual property</h2>
      <p>
        The Service, including its design, branding, and the "PROTBYTE" and "PROTBYTE
        Sentinel" names and logos, is owned by PROTBYTE and protected by
        applicable intellectual-property laws. The underlying source code is made
        available separately under the MIT License on{' '}
        <a href="https://github.com/masoud-heydari/protbyte-sentinel" target="_blank" rel="noreferrer">GitHub</a>;
        your use of that source code is governed by that license, not these Terms.
        Subject to these Terms, you may use a report generated about a domain you are
        authorized to assess for your own internal purposes.
      </p>

      <h2>10. No persistence of scan results</h2>
      <p>
        Scan results are returned to your browser and are not stored on our servers.
        The result is held only in your browser for the current session — a refresh
        or the back button preserves it, and closing the tab discards it. We do not
        offer accounts, scan history, or change-monitoring features. We keep an aggregate,
        IP-free operator log (for example, domain, timestamp, scores, severity
        counts, scan duration, status, and Cloudflare-attributed country) for
        service-quality and abuse monitoring; see our{' '}
        <a href="/privacy">Privacy Policy</a> for the full list and an opt-out email
        address.
      </p>

      <h2>11. Third-party services</h2>
      <p>
        The Service depends on third-party services we do not control, including
        Cloudflare (hosting, DNS-over-HTTPS, edge cache, KV, D1, Access, and
        Turnstile), the IANA RDAP bootstrap registry and TLD-operated RDAP servers,
        certspotter and crt.sh (Certificate Transparency mirrors), Team Cymru
        (IP-to-ASN), and OSV.dev (CVE database). Their availability and behavior are governed by their own terms
        and privacy policies, and we are not responsible for them.
      </p>

      <h2>12. Changes to the Service and these Terms</h2>
      <p>
        We may modify, suspend, or discontinue any part of the Service at any time,
        with or without notice. We may update these Terms from time to time; the
        updated version takes effect when posted at this URL, and the "Last updated"
        date will change. Your continued use of the Service after a change
        constitutes acceptance of the revised Terms.
      </p>

      <h2>13. Termination</h2>
      <p>
        We may suspend or terminate your access to the Service at any time, for any
        reason, including violation of these Terms or the Acceptable Use Policy.
        Sections 4 through 9, 11, and 14 through 15, and any other provision that by
        its nature should survive, will survive any termination.
      </p>

      <h2>14. Governing law and dispute resolution</h2>
      <p>
        These Terms and any dispute arising out of or relating to them or the
        Service are governed by the laws of the <strong>State of Texas, USA</strong>,
        without regard to its conflict-of-laws principles. The Service is available
        globally, but you are responsible for complying with all laws applicable to
        you, and you may not use the Service where prohibited by law.
      </p>
      <p>
        You agree that the exclusive venue for any dispute that is not subject to
        the arbitration or waiver provisions below will be the state and federal
        courts located in Texas, USA, and you consent to the personal jurisdiction
        of those courts. To the maximum extent permitted by law, you and PROTBYTE
        each waive any right to a jury trial and agree that any claim will be
        brought only in an individual capacity, and not as a plaintiff or class
        member in any purported class or representative proceeding.
      </p>

      <h2>15. Miscellaneous</h2>
      <p>
        These Terms, together with the Acceptable Use Policy and Privacy Policy,
        constitute the entire agreement between you and PROTBYTE regarding the
        Service and supersede any prior agreements. If any provision is held
        unenforceable, the remaining provisions remain in full effect, and the
        unenforceable provision will be limited or eliminated to the minimum extent
        necessary. Our failure to enforce any provision is not a waiver of it. You
        may not assign these Terms without our prior written consent; we may assign
        them freely. We are not liable for any delay or failure to perform caused by
        events beyond our reasonable control.
      </p>

      <h2>16. Contact</h2>
      <p>
        Questions about these Terms? Email us at{' '}
        <a href="mailto:contact@protbyte.com">contact@protbyte.com</a>.
      </p>
    </LegalLayout>
  );
}
